import axios from "axios";
import { modalInfo } from "../modal-info/modal-info";

const CLASS_PREFIX = "interactive-player";
const DATA_PREFIX = "data-interactive-player";

/**
 * Утилита для выборки элементов по data-атрибутам
 */
function queryDataAttr(attr, container = document) {
    return container.querySelector(`[${DATA_PREFIX}-${attr}]`);
}

class InteractivePlayer {
    constructor($container) {
        this.$container = $container;
        this.classPrefix = CLASS_PREFIX;
        this.dataPrefix = DATA_PREFIX;
        this.videoDetails = {
            id: null,
            url: "",
            questions: [],
            responded_questions: [],
            total: 0,
            is_demo: false,
        };
        this.answeredPausePoints = new Set();
        this.currentPausePointIndex = 0;
        this.currentPointIndex = 0;
        this.isStarted = false;
        this.isSelected = false;
        // Если понадобится навигация, можно инициализировать isManualNavigation, иначе убираем её использование
        // this.isManualNavigation = false;

        // Сохраняем привязанный обработчик, чтобы потом корректно удалить его
        this.boundContinueVideo = this.continueVideo.bind(this);

        this.initElements();
        this.bindEvents();
        this.init();
    }

    /**
     * Инициализация ссылок на элементы DOM
     */
    initElements() {
        // Элементы плеера и загрузчика
        this.$player = queryDataAttr("video", this.$container);
        this.$loader = queryDataAttr("loader", this.$container);

        // Элементы таймлайна – ищем внутри контейнера, чтобы не затрагивать другие плееры
        this.$timeline = queryDataAttr("timeline", this.$container);
        this.$timelineProgress = queryDataAttr("timeline-progress", this.$timeline);
        this.$timelineMarkersContainer = queryDataAttr("timeline-markers", this.$timeline);

        // Элементы оверлея с вопросом
        this.$questionOverlay = queryDataAttr("question-overlay", this.$container);
        this.$questionOverlayContainer = queryDataAttr("question-overlay-container", this.$container);
        this.$questionLabel = queryDataAttr("question-total", this.$container);
        this.$questionText = queryDataAttr("question-text", this.$container);
        this.$questionAnswersContainer = queryDataAttr("question-answers", this.$container);
        this.$answerText = queryDataAttr("answer-text", this.$container);
        this.$retryBtn = queryDataAttr("retry-btn", this.$container);
        this.$continueBtn = queryDataAttr("continue-btn", this.$container);

        // Дополнительные элементы вопросов
        this.$questionTitle = queryDataAttr("question-title", this.$container);
        this.$questionDesc = queryDataAttr("question-desc", this.$container);

        // Элементы стартового оверлея
        this.$startOverlay = queryDataAttr("start-overlay", this.$container);
        this.$startBtn = queryDataAttr("start-btn", this.$container);
        this.$startBtnText = queryDataAttr("start-btn-text", this.$container);

        // Элементы управления плеером
        this.$controlOverlay = queryDataAttr("player-control", this.$container);
        this.$controlPause = queryDataAttr("player-control-pause", this.$container);
        this.$controlPlay = queryDataAttr("player-control-play", this.$container);
        this.$controlPlayBtn = queryDataAttr("player-control-play-btn", this.$container);
        this.$controlPlayNext = queryDataAttr("player-control-next", this.$container);
        this.$controlPlayPrev = queryDataAttr("player-control-prev", this.$container);

        // Шаблон финального экрана (находится в document, если он общий для всех плееров)
        this.$finalContainerTemplate = queryDataAttr("final-template", document);
    }

    /**
     * Привязка событий к элементам
     */
    bindEvents() {
        // События плеера
        this.$player.addEventListener("canplaythrough", () => {
            this.$loader.style.display = "none";
        });
        this.$player.addEventListener("timeupdate", this.handlePlayerTime.bind(this));
        this.$player.addEventListener("ended", this.handleVideoEnd.bind(this));

        // События оверлея вопросов
        this.$continueBtn.addEventListener("click", this.boundContinueVideo);
        this.$retryBtn.addEventListener("click", this.retrySegment.bind(this));

        // События стартового экрана
        this.$startBtn.addEventListener("click", this.handleStart.bind(this));

        // События управления плеером
        this.$controlPlayBtn.addEventListener("click", this.handlePlay.bind(this));
        this.$controlPause.addEventListener("click", this.handlePause.bind(this));
        this.$controlPlayNext.addEventListener("click", this.handlePlayNext.bind(this));
        this.$controlPlayPrev.addEventListener("click", this.handlePlayPrev.bind(this));
    }

    /**
     * Основная инициализация плеера:
     * - отключение стандартных контролов
     * - загрузка данных видео и вопросов
     * - установка времени видео для уже отвеченных вопросов (если есть)
     * - инициализация таймлайна
     */
    async init() {
        this.$player.controls = false;
        this.videoDetails.id = window.currentVideo;
        this.$controlPlayBtn.style.display = "flex";
        this.$controlPlay.style.display = "none";

        await this.getVideoDetails();
        await this.getRespondedUserQuestion();
        await this.setInitialVideoTime();
        await this.initTimeline();
        console.log('PLAYER START');
        this.showlogs();
    }

    showlogs(){
        console.log('------------------------');
        console.log('this.answeredPausePoints', this.answeredPausePoints);
        console.log('this.currentPausePointIndex', this.currentPausePointIndex);
        console.log('this.currentPointIndex', this.currentPointIndex);
        // Убираем несуществующее свойство isManualNavigation:
        // console.log('this.isManualNavigation', this.isManualNavigation);
        console.log('this.videoDetails.questions', this.videoDetails.questions);
        console.log('this.videoDetails.responded_questions', this.videoDetails.responded_questions);
        console.log('------------------------');
    }

    /**
     * Загрузка данных видео с сервера
     */
    async getVideoDetails() {
        try {
            const response = await axios.get(`/api/video/${this.videoDetails.id}`);
            if (response.data) {
                this.videoDetails = {
                    ...this.videoDetails,
                    url: response.data.url,
                    questions: response.data.questions,
                    total: response.data.total,
                    is_demo: response.data.is_demo,
                };
                const $source = this.$player.querySelector("source");
                if ($source) {
                    $source.setAttribute("src", this.videoDetails.url);
                    this.$player.load();
                } else {
                    console.error("Элемент <source> не найден внутри плеера.");
                }
            }
        } catch (error) {
            console.error("Ошибка загрузки данных видео:", error);
        }
    }

    /**
     * Загрузка ранее отвеченных вопросов пользователя
     */
    async getRespondedUserQuestion() {
        try {
            const response = await axios.get(`/api/video/${this.videoDetails.id}/responded-questions`);
            if (response.data) {
                this.videoDetails = {
                    ...this.videoDetails,
                    responded_questions: response.data.questions,
                };
            }
        } catch (error) {
            console.error("Ошибка загрузки данных:", error);
        }
    }

    /**
     * Установка начального времени воспроизведения видео, если есть предыдущие ответы
     */
    async setInitialVideoTime() {
        if (this.videoDetails.responded_questions.length > 0) {
            this.$startBtnText.innerText = "Продолжить просмотр";

            // Собираем индексы отвеченных вопросов
            const answeredQuestionIndexes = this.videoDetails.responded_questions
                .map(q => this.videoDetails.questions.findIndex(question => question.id === q.id))
                .filter(idx => idx !== -1)
                .sort((a, b) => a - b);

            // Последний отвеченный индекс
            const lastAnsweredIndex = answeredQuestionIndexes[answeredQuestionIndexes.length - 1];

            // Ищем следующий неотвеченный сегмент после последнего отвеченного
            let nextUnansweredIndex = lastAnsweredIndex + 1;
            while (
                nextUnansweredIndex < this.videoDetails.questions.length &&
                answeredQuestionIndexes.includes(nextUnansweredIndex)
                ) {
                nextUnansweredIndex++;
            }

            // Если найден неотвеченный сегмент, используем его; иначе остаёмся на последнем отвеченном
            if (nextUnansweredIndex < this.videoDetails.questions.length) {
                this.currentPausePointIndex = nextUnansweredIndex;
            } else {
                this.currentPausePointIndex = lastAnsweredIndex;
            }

            // Обновляем текущий индекс сегмента как последний отвеченный
            this.currentPointIndex = lastAnsweredIndex;

            // Устанавливаем время видео на время последнего отвеченного сегмента
            const lastPauseTime = this.videoDetails.questions[lastAnsweredIndex].pause_time;
            this.$player.currentTime = lastPauseTime;

            // Сохраняем набор отвеченных индексов
            this.answeredPausePoints = new Set(answeredQuestionIndexes);
        } else {
            this.currentPointIndex = 0;
            this.currentPausePointIndex = 0;
        }
    }

    /**
     * Инициализация таймлайна: расстановка маркеров согласно pause_time вопросов
     */
    async initTimeline() {
        const duration = this.$player.duration;
        if (!duration || isNaN(duration) || this.videoDetails.questions.length === 0) {
            this.$player.addEventListener("loadedmetadata", this.initTimeline.bind(this), { once: true });
            return;
        }
        this.$timelineMarkersContainer.innerHTML = "";
        this.videoDetails.questions.forEach((question, index) => {
            const marker = document.createElement("div");
            marker.classList.add(`${this.classPrefix}__timeline-marker`);
            const posPercent = (question.pause_time / duration) * 100;
            marker.style.left = `${posPercent}%`;
            marker.setAttribute("data-question-index", index);
            this.$timelineMarkersContainer.appendChild(marker);
        });
    }

    /**
     * Обновление визуального прогресса таймлайна
     */
    updateTimelineProgress() {
        const duration = this.$player.duration;
        if (!duration) return;
        const progressPercent = (this.$player.currentTime / duration) * 100;
        this.$timelineProgress.style.width = `${progressPercent}%`;
    }

    /**
     * Обработка события timeupdate плеера: проверка достижения точки с вопросом
     */
    handlePlayerTime() {
        this.updateTimelineProgress();

        if (!this.isStarted) { return; }

        const currentTime = this.$player.currentTime;
        if (this.videoDetails.questions.length > 0) {
            const segmentIndex = this.findSegmentIndex(currentTime, this.videoDetails.questions);
            this.currentPointIndex = segmentIndex;
        } else {
            this.currentPointIndex = 0;
        }

        console.log('currentPointIndex: ' + this.currentPointIndex + ' | currentPausePointIndex: ' + this.currentPausePointIndex);

        if (this.currentPointIndex >= this.currentPausePointIndex) {
            const pauseTime = this.videoDetails.questions[this.currentPausePointIndex]?.pause_time;
            if (pauseTime !== undefined && currentTime >= pauseTime) {
                this.$player.pause();
                this.showQuestion(this.videoDetails.questions[this.currentPausePointIndex], this.currentPausePointIndex);
            }
        }

        this.checkNavButtonEnable();
    }

    /**
     * Находим текущий индекс сегмента
     */
    findSegmentIndex(currentTime, questions) {
        let low = 0, high = questions.length;
        while (low < high) {
            const mid = low + Math.floor((high - low) / 2);
            if (currentTime < questions[mid].pause_time) {
                high = mid;
            } else {
                low = mid + 1;
            }
        }
        return low;
    }

    /**
     * Отображение вопроса в момент паузы видео
     */
    showQuestion(question, index) {
        const currentQuestionIndex = index + 1;
        if (currentQuestionIndex !== 1) {
            this.$questionTitle.style.display = "none";
            this.$questionDesc.style.display = "none";
        }
        this.$questionLabel.innerText = `ВОПРОС ${currentQuestionIndex} ИЗ ${this.videoDetails.total}`;
        this.$questionText.textContent = question.question_text;
        this.$questionAnswersContainer.innerHTML = "";
        this.currentPointIndex = index;
        question.video_answers.forEach((answer) => {
            const button = document.createElement("button");
            button.textContent = answer.text;
            button.setAttribute(`${this.dataPrefix}-correct`, answer.is_correct);
            button.onclick = () => this.selectAnswer(answer.is_correct, question.response_text, button, question.id, answer.id);
            button.classList.add(`${this.classPrefix}__answers-option`);
            this.$questionAnswersContainer.appendChild(button);
        });
        this.$questionOverlay.classList.add(`${this.classPrefix}__overlay--active`);
    }

    /**
     * Обработка выбора ответа пользователем
     */
    async selectAnswer(isCorrect, responseText, selectedButton, questionId, answerId) {
        if (this.isSelected) return;
        this.isSelected = true;
        const buttons = this.$questionAnswersContainer.querySelectorAll(`.${this.classPrefix}__answers-option`);
        buttons.forEach((button) => {
            const buttonIsCorrect = button.getAttribute(`${this.dataPrefix}-correct`) === "1";
            if (button === selectedButton) {
                button.classList.add(isCorrect
                    ? `${this.classPrefix}__answers-option--correct`
                    : `${this.classPrefix}__answers-option--select`);
            } else if (buttonIsCorrect) {
                button.classList.add(`${this.classPrefix}__answers-option--correct`);
            } else {
                button.classList.add(`${this.classPrefix}__answers-option--fade`);
            }
        });
        this.$answerText.style.whiteSpace = 'pre-wrap';
        this.$answerText.textContent = responseText;
        this.$continueBtn.style.display = "flex";
        this.$retryBtn.style.display = "none";
        await this.saveAnswer(questionId, answerId);

        // Вычисляем следующий неотвеченный индекс. Если не найден, остаёмся на текущем
        let nextIndex = this.videoDetails.questions.findIndex(
            (q, index) => index > this.currentPausePointIndex && !this.answeredPausePoints.has(index)
        );
        if (nextIndex === -1) {
            // Если вопросов больше нет, установим индекс за концом массива вопросов.
            this.currentPausePointIndex = this.videoDetails.questions.length;
        } else {
            this.currentPausePointIndex = nextIndex;
        }
    }

    /**
     * Продолжение воспроизведения видео после ответа на вопрос
     */
    continueVideo() {
        this.isSelected = false;
        this.answeredPausePoints.add(this.currentPointIndex);
        this.$questionOverlay.classList.remove(`${this.classPrefix}__overlay--active`);
        this.$continueBtn.style.display = "none";
        this.$retryBtn.style.display = "flex";
        this.$answerText.textContent = "";
        this.$player.play();
    }

    /**
     * Повтор сегмента видео при неправильном ответе
     */
    retrySegment() {
        this.$questionOverlay.classList.remove(`${this.classPrefix}__overlay--active`);
        if (this.currentPausePointIndex > 0) {
            this.$player.currentTime = this.videoDetails.questions[this.currentPausePointIndex - 1].pause_time;
        } else {
            this.$player.currentTime = 0;
        }
        this.updateTimelineProgress();
        this.$player.play();
    }

    /**
     * Обработка окончания видео
     */
    handleVideoEnd() {
        this.$questionOverlayContainer.innerHTML = "";
        this.$questionOverlay.classList.add(`${this.classPrefix}__overlay--active`);
        // Удаляем обработчик с использованием сохранённой ссылки
        this.$continueBtn.removeEventListener("click", this.boundContinueVideo);
        if (this.videoDetails.is_demo) {
            modalInfo(this.$questionOverlayContainer);
        } else {
            const $finalContainerClone = document.importNode(this.$finalContainerTemplate.content, true);
            const $finalTitle = $finalContainerClone.querySelector(`[${this.dataPrefix}-final-title]`);
            const $finalText = $finalContainerClone.querySelector(`[${this.dataPrefix}-final-text]`);
            $finalTitle.innerText = "Вы завершили видео";
            $finalText.innerText = "Теперь вы можете перейти к результатам";
            this.$questionOverlayContainer.appendChild($finalContainerClone);
            this.$continueBtn.addEventListener("click", () => location.reload());
        }
        this.$continueBtn.style.display = "flex";
    }

    /**
     * Сохранение ответа пользователя через API
     */
    saveAnswer(questionId, answerId) {
        return axios.post("/answer/save", {
            question_id: questionId,
            answer_id: answerId,
        })
            .then((response) => {
                // Ответ сохранён успешно
            })
            .catch((error) => {
                console.error("Ошибка при сохранении ответа:", error);
            });
    }

    // Методы управления плеером

    handleStart() {
        this.$player.play();
        this.$startOverlay.style.display = "none";
        this.$controlPlay.style.display = "none";
        this.$controlPause.style.display = "flex";
        this.isStarted = true;
        this.showlogs();
    }

    handlePlay() {
        this.$player.play();
        this.$controlPlay.style.display = "none";
        this.$controlPause.style.display = "flex";
    }

    handlePause() {
        this.$player.pause();
        this.$controlPause.style.display = "none";
        this.$controlPlay.style.display = "flex";
        this.checkNavButtonEnable();
    }

    checkNavButtonEnable(){
        if (this.currentPointIndex >= this.currentPausePointIndex) {
            this.$controlPlayNext.classList.add(`${this.classPrefix}__player-control-btn--inactive`);
        } else {
            this.$controlPlayNext.classList.remove(`${this.classPrefix}__player-control-btn--inactive`);
        }
    }

    /**
     * Переход к следующему сегменту, если следующий вопрос уже отвечен.
     */
    handlePlayNext() {
        if (this.currentPointIndex >= this.currentPausePointIndex) {
            return; // Нельзя переходить вперёд, если есть неотвеченные вопросы
        }
        this.$player.currentTime = this.videoDetails.questions[this.currentPointIndex].pause_time;
        this.updateTimelineProgress();
        this.checkNavButtonEnable();
    }

    /**
     * Переход к предыдущему сегменту, если предыдущий вопрос уже отвечен.
     * Если индекс для перехода меньше 0 или сегмент не отмечен, перемещаемся в начало.
     */
    handlePlayPrev() {
        if (this.currentPointIndex === null || this.currentPointIndex === 0) {
            this.$player.currentTime = 0;
            return;
        }
        const rePrevIndex = this.currentPointIndex - 2;
        if (rePrevIndex < 0 || !this.answeredPausePoints.has(rePrevIndex)) {
            this.$player.currentTime = 0;
            return;
        }
        const newTime = this.videoDetails.questions[rePrevIndex].pause_time;
        this.$player.currentTime = newTime;
        this.updateTimelineProgress();
        this.showlogs();
        this.checkNavButtonEnable();
    }
}

// Инициализация плеера, если найден контейнер
const container = document.querySelector(`[${DATA_PREFIX}-container]`);
if (container) {
    new InteractivePlayer(container);
}

export { InteractivePlayer };
